import { subjectsFor } from "../types";
import type { Child, Entry, Program } from "../types";

export interface GapInput {
  child: Child;
  entries: Entry[];
  program: Program;
  /** Epoch milliseconds. */
  now: number;
}

const DAY_MS = 86_400_000;
const QUIET_DAYS = 7;

/** Fixed gap wording. Other modules match on these to rephrase a gap for a parent. */
export const GAP = {
  nothingLogged: "nothing logged yet",
  noMinutes: "no minutes recorded, so hours per week cannot be computed",
  noMastery: "the method of determining mastery is empty",
  noMaterials: "no materials are listed",
  untaggedPrefix: "no entries tagged for ",
  quietPrefix: "no entries in the last ",
  quietSuffix: " days",
} as const;

/** This child's entries, leaving out the sample entries the ledger seeds for a new family. */
export function realEntriesFor(childId: string, entries: readonly Entry[]): Entry[] {
  return entries.filter((e) => e.childId === childId && e.example !== true);
}

function subjectsWithoutEntries(child: Child, entries: readonly Entry[]): string[] {
  const tagged = new Set(entries.flatMap((e) => e.subjects));
  return subjectsFor(child.grade).filter((subject) => !tagged.has(subject));
}

function daysSinceNewest(entries: readonly Entry[], now: number): number {
  const newest = entries.reduce((latest, e) => Math.max(latest, e.ts), -Infinity);
  return Math.floor((now - newest) / DAY_MS);
}

export function findGaps({ child, entries, program, now }: GapInput): string[] {
  const mine = realEntriesFor(child.id, entries);
  if (mine.length === 0) return [GAP.nothingLogged];

  const gaps: string[] = [];
  if (!mine.some((e) => e.minutes > 0)) gaps.push(GAP.noMinutes);
  if (program.mastery.trim() === "") gaps.push(GAP.noMastery);
  if (program.materials.length === 0) gaps.push(GAP.noMaterials);

  const notCovered = subjectsWithoutEntries(child, mine);
  if (notCovered.length > 0) gaps.push(GAP.untaggedPrefix + notCovered.join(", "));

  const quietDays = daysSinceNewest(mine, now);
  if (quietDays >= QUIET_DAYS) gaps.push(GAP.quietPrefix + quietDays + GAP.quietSuffix);

  return gaps;
}
